import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../types';

export type RoomSelectionType =
  | 'hotel_suit'
  | 'bdsm_dungeon'
  | 'private_strip_club_room'
  | 'shower'
  | 'naughty_classroom'
  | 'massage_room'
  | 'pool_at_night';

export interface RoomData {
  image: string;
  firstMessage: string;
  secondMessage: string;
}

export const ROOM_DATA: Record<RoomSelectionType, RoomData> = {
  hotel_suit: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/hotel-suite.png`,
    firstMessage: 'Silk sheets... I only booked this for us. Get in.',
    secondMessage: '*She pats the bed, eyes burning with desire...*',
  },
  bdsm_dungeon: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/bdsm-dungeon.png`,
    firstMessage: "Chains, cuffs, me. You're mine now.",
    secondMessage: '*She snaps the whip against the wall — hard...*',
  },
  private_strip_club_room: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/private-strip-club.png`,
    firstMessage: 'Private show, just for you. Tell me what to take off first.',
    secondMessage: '*She grips the pole and slides down, slow...*',
  },
  shower: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/shower.png`,
    firstMessage: "The water's hot... but I'm hotter. Coming in?",
    secondMessage: '*Her hand wipes the fogged glass, revealing bare skin...*',
  },
  naughty_classroom: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/naughty-classroom.png`,
    firstMessage: 'Bad boy... close the door before I punish you.',
    secondMessage: '*She slaps the ruler on the desk with a smirk...*',
  },
  massage_room: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/massage-room.png`,
    firstMessage: "Strip. Lay down. I'll take care of everything.",
    secondMessage: '*Warm oil drips from her fingers onto your skin...*',
  },
  pool_at_night: {
    image: `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/pool-at-night.png`,
    firstMessage: 'Wet, hot, waiting. Get in with me.',
    secondMessage: '*She parts her legs under the bubbling water...*',
  },
};

/**
 * Returns the background image path for a given room selection
 * @param roomSelection - The selected room type
 * @returns The path to the background image
 */
const getRoomBackgroundImagePath = (
  roomSelection: RoomSelectionType | null,
): string => {
  switch (roomSelection) {
    case 'hotel_suit':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/hotel-suite.png`;
    case 'bdsm_dungeon':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/bdsm-dungeon.png`;
    case 'private_strip_club_room':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/private-strip-club.png`;
    case 'shower':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/shower.png`;
    case 'naughty_classroom':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/naughty-classroom.png`;
    case 'massage_room':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/massage-room.png`;
    case 'pool_at_night':
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/pool-at-night.png`;
    default:
      return `${S3_BUCKET_URL}/own-ai-roleplay-rooms/backgrounds/naughty-classroom.png`;
  }
};

export const useRoomBackgroundImage = (): string => {
  const form = useFormContext<LayoutDataType>();
  const roomSelection = form.watch('room_selection');

  const backgroundImagePath = getRoomBackgroundImagePath(roomSelection);

  preloadImage(backgroundImagePath);

  return backgroundImagePath;
};

export const useRoomData = (): RoomData => {
  const form = useFormContext<LayoutDataType>();
  const roomSelection = form.watch('room_selection');

  if (!roomSelection) {
    return ROOM_DATA.naughty_classroom;
  }

  const roomData = ROOM_DATA[roomSelection];
  return roomData;
};
